/**
 * The CB `actionKey`s that have a specialized React renderer — as a bare set,
 * with NO component imports.
 *
 * ## Why this exists separately from `registry.js`
 *
 * `registry.js` answers the same question, and asking it there is the obvious
 * thing to do. But it STATICALLY imports all 20+ specialized page components, so
 * `import { getRendererForActionKey } from './registry'` anywhere pulls the
 * entire specialized-report graph — the pages, their services, their grids — into
 * that module's bundle. Doing it from the Report Queue's job drawer dragged
 * `specializedReportService` and `accountingReportService` into the queue's
 * dependency tree, which surfaced immediately as three unrelated test suites
 * failing to even LOAD ("No REPORT_RUN_TIMEOUT_MS export is defined on the
 * @api/v1/client mock"). The bundle cost was the same, just quieter.
 *
 * So: the keys live here, the components live there, and a caller that only needs
 * to know WHETHER a report is specialized pays a few hundred bytes instead of the
 * whole feature.
 *
 * ## Why this is not a second source of truth
 *
 * `registry.js` imports this list and `registry.test.js` pins that its own map's
 * keys are exactly this set — so adding a renderer without adding its key here,
 * or vice versa, fails a test rather than silently diverging. That check is the
 * only thing making two files safe; do not delete it.
 *
 * ## What the answer is used for
 *
 * Chiefly: whether re-running a saved run from the queue drawer will do anything.
 * `ReportView`'s `?rerun=1` effect returns early for a report with a registered
 * renderer (`ReportView.jsx:702`), and no specialized page reads `rerun`, so on
 * these reports "Run again" reopens the snapshot and issues no run at all.
 */

export const SPECIALIZED_ACTION_KEYS = new Set([
  'cbARReportView',
  'cbTimeEntriesReportView',
  'cbBillingRealizationReportHomeView',
  'cbCalendarReportHomeView',
  'cbAccountsPayableReport',
  'cbAPDetailReportView',
  'cbAccountingTrialBalanceReport',
  'cbAccountingPAndLReport',
  'cbAccountingPAndLComparisonReport',
  'cbAccountingGLActualVsBudgetReport',
  'cbAccountingBalanceSheet',
  'cbAccountingGeneralLedgerReport',
  'cbBillJournalReportSummaryHomeView',
  'cbFeeAllocationAttorneyReportView',
  'cbRevenueAndCommissionsView',
  'cbPaymentsByCaseView',
  'cbDailyReportView',
  // CB uses both keys for matter metrics depending on tenant config.
  'cbMatterMetricReportHomeView',
  'cbMatterMetricReport',
  'cbAccountingReconciliationHistory',
  'cbTimekeeperAnalysisView',
  'cbMonthlyProductivityView',
  'cbFeeReceiptView',
  // The three CB Payments views and Exago are non-ExtJS EXTERNAL products handed
  // off in a new tab. They are still "specialized" for this question's purpose:
  // they do not travel the generic engine, so a rerun does nothing there either.
  'cbCBPaymentsPaymentsView',
  'cbCBPaymentsPayoutsView',
  'cbPaymentsReconciliationHomeView',
  'cbExagoReportsHomeView',
])

/** Does this report render through a specialized page rather than the generic engine? */
export const isSpecializedActionKey = (actionKey) =>
  !!actionKey && SPECIALIZED_ACTION_KEYS.has(actionKey)
